import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  FilterStrip,
  ResultFooter,
  UnavailableNotice,
  WindowLine,
} from "../components/filters/ConsoleFilterContract";
import {
  CONSOLE_WINDOW_SCANS,
  REPORTS_SELF_SERVE_UNAVAILABLE_ACTIONS,
  reportsFilterChips,
  reportsResultCopy,
  type ConsoleFilterChipLabel,
  type ConsoleUnavailableAction,
} from "../data/console-filter-contract";
import {
  buildScanReportCsv,
  createScanReportBlob,
  reportColumnsMetadataLabel,
  reportFileName,
  reportMethodsMetadataLabel,
  reportSortMetadataLabel,
  reportStatusesMetadataLabel,
} from "../data/report-export";
import {
  DEFAULT_REPORT_COLUMNS,
  DEFAULT_REPORT_DATE_SELECTION,
  DEFAULT_REPORT_SORT,
  REPORT_CHECKPOINT_WINDOW_HINT,
  REPORT_COLUMN_KEYS,
  REPORT_COLUMN_LABELS,
  REPORT_DATE_PRESETS,
  REPORT_LIFECYCLE_LABELS,
  REPORT_LIFECYCLE_STATES,
  REPORT_METHOD_LABELS,
  REPORT_PICKER_FLOOR_COPY,
  REPORT_SCAN_METHODS,
  REPORT_SITE_SELECTION_MAX,
  REPORT_SUPPORT_CSV_UNAVAILABLE_COPY,
  REPORT_TOO_MANY_SITES_COPY,
  isReportCustomRangeSelectable,
  mergeScanLabels,
  reportDateMetadataLabel,
  reportDateSelectionKey,
  reportPickerBounds,
  reportSitesChipValue,
  toggleReportSite,
  uniqueScanLabels,
  type ReportColumnKey,
  type ReportDatePreset,
  type ReportDateSelection,
  type ReportScanFilterSets,
  type ReportScanLifecycleState,
  type ReportScanMethod,
  type ReportScanSort,
} from "../data/report-scan-filters";
import {
  listMyReportViews,
  upsertMyReportView,
  validateMyReportViewLabels,
  type ReportSavedView,
} from "../data/report-saved-views";
import {
  REPORT_SCAN_LIMIT,
  checkpointDisplayLabel,
  fetchReportScanList,
  gpsQualityLabel,
  sortReportScanRows,
  type ReportScanRead,
  type ScanRow,
} from "../data/scan-list";
import { useReadModel } from "../data/useReadModel";
import { useAnalytics } from "../lib/analytics-context";
import { formatDisplayTime } from "../lib/display-time";
import { deliverBlobFile } from "../lib/file-delivery";
import { useSupportSession } from "../support/SupportSessionProvider";

type ReportLoad =
  | { readonly kind: "read"; readonly value: ReportScanRead }
  | { readonly kind: "error"; readonly message: string };

interface KeyedReportLoad {
  readonly client: SupabaseClient;
  readonly requestKey: string;
  readonly outcome: ReportLoad;
}

type ExportState =
  | { readonly kind: "idle" }
  | { readonly kind: "preparing" }
  | { readonly kind: "error" }
  | { readonly kind: "refused"; readonly message: string }
  | { readonly kind: "success"; readonly fileName: string };

type ViewsLoad =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly views: readonly ReportSavedView[] }
  | { readonly kind: "unavailable" };

type StaleLabel = {
  readonly family: string;
  readonly label: string;
};

type StaleViewState =
  | {
      readonly kind: "stale";
      readonly message: string;
      readonly unmatched: readonly StaleLabel[];
      readonly view: ReportSavedView;
    }
  | { readonly kind: "unavailable"; readonly message: string };

type PendingViewValidation = {
  readonly attempt: number;
  readonly client: SupabaseClient;
  readonly requestKey: string;
  readonly view: ReportSavedView;
};

/** Columns and sort only reorder the retained window; they do not change its host query. */
function reportRequestKey(accountId: string, timeZone: string, supportScope: string, filters: ReportScanFilterSets): string {
  return JSON.stringify({
    accountId,
    siteNames: filters.siteNames,
    guardFilter: filters.guardName ?? "",
    routeName: filters.routeName ?? "",
    checkpointName: filters.checkpointName ?? "",
    methods: filters.methods,
    presenceAdhocOnly: filters.presenceAdhocOnly,
    lifecycleStates: filters.lifecycleStates,
    reviewRequiredOnly: filters.reviewRequiredOnly,
    dateKey: reportDateSelectionKey(filters.dateSelection),
    timeZone,
    supportScope,
  });
}

const systemClock = (): Date => new Date();

function toggleEnumValue<T extends string>(current: readonly T[], value: T): T[] {
  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
}

function visibleColumns(columns: readonly ReportColumnKey[]): ReportColumnKey[] {
  return columns.length > 0 ? [...columns] : [...DEFAULT_REPORT_COLUMNS];
}

function cellText(row: ScanRow, column: ReportColumnKey, timeZone: string): string {
  switch (column) {
    case "captured":
      return formatDisplayTime(row.deviceTime, timeZone);
    case "received":
      return formatDisplayTime(row.serverReceivedAt, timeZone);
    case "checkpoint":
      return checkpointDisplayLabel(row);
    case "site":
      return row.siteName ?? "Not available";
    case "route":
      return row.routeName ?? "Not available";
    case "guard":
      return row.guardName ?? "Not available";
    case "method":
      return row.scanMethod;
    case "status":
      return row.lifecycleState;
    case "gps":
      return gpsQualityLabel(row);
    case "validation_notes":
      return row.validationNotes ?? "";
  }
}

// Reports export surface (read-only) over supervisor_scan_list. Enterprise filters are
// applied on the host before the 500-row view/export window. The keyed result envelope keeps
// useReadModel's retained payload from being exported under a newly selected filter label.
export function ReportsExport({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  clock = systemClock,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  clock?: () => Date;
}) {
  const support = useSupportSession();
  const initialBounds = reportPickerBounds(timeZone, clock());
  const [siteNames, setSiteNames] = useState<readonly string[]>([]);
  const [siteCapNotice, setSiteCapNotice] = useState<string | null>(null);
  const [guardFilter, setGuardFilter] = useState("");
  const [routeName, setRouteName] = useState("");
  const [checkpointName, setCheckpointName] = useState("");
  const [methods, setMethods] = useState<readonly ReportScanMethod[]>([]);
  const [presenceAdhocOnly, setPresenceAdhocOnly] = useState(false);
  const [lifecycleStates, setLifecycleStates] = useState<readonly ReportScanLifecycleState[]>(
    [],
  );
  const [reviewRequiredOnly, setReviewRequiredOnly] = useState(false);
  const [columns, setColumns] = useState<readonly ReportColumnKey[]>(DEFAULT_REPORT_COLUMNS);
  const [sort, setSort] = useState<ReportScanSort>(DEFAULT_REPORT_SORT);
  const [dateSelection, setDateSelection] = useState<ReportDateSelection>(
    DEFAULT_REPORT_DATE_SELECTION,
  );
  const [customOpen, setCustomOpen] = useState(false);
  const [customFromDay, setCustomFromDay] = useState(initialBounds.floorDay);
  const [customToDay, setCustomToDay] = useState(initialBounds.todayDay);
  const [siteOptions, setSiteOptions] = useState<string[]>([]);
  const [guardOptions, setGuardOptions] = useState<string[]>([]);
  const [routeOptions, setRouteOptions] = useState<string[]>([]);
  const [checkpointOptions, setCheckpointOptions] = useState<string[]>([]);
  const [exportState, setExportState] = useState<ExportState>({ kind: "idle" });
  const [viewsMenuOpen, setViewsMenuOpen] = useState(false);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [viewsLoad, setViewsLoad] = useState<ViewsLoad>({ kind: "loading" });
  const [defaultApplied, setDefaultApplied] = useState(false);
  const [staleView, setStaleView] = useState<StaleViewState | null>(null);
  const [pendingValidation, setPendingValidation] = useState<PendingViewValidation | null>(null);
  const validationAttempt = useRef(0);
  const analytics = useAnalytics();
  const customPanelId = useId();
  const customTriggerRef = useRef<HTMLButtonElement | null>(null);
  const viewsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const guardRemoveRef = useRef<HTMLButtonElement | null>(null);
  const sitesRemoveRef = useRef<HTMLButtonElement | null>(null);

  const dateKey = reportDateSelectionKey(dateSelection);
  const supportScope = support.loading ? "loading" : support.session?.sessionId ?? "direct";
  const requestKey = reportRequestKey(accountId, timeZone, supportScope, {
    siteNames, guardName: guardFilter || null, routeName: routeName || null,
    checkpointName: checkpointName || null, methods, presenceAdhocOnly, lifecycleStates,
    reviewRequiredOnly, dateSelection, columns, sort,
  });
  const currentRequestKeyRef = useRef(requestKey);
  currentRequestKeyRef.current = requestKey;
  const currentClientRef = useRef(client);
  currentClientRef.current = client;

  const supportActive = !support.loading && support.session !== null;
  const supportLoading = support.loading;

  const { status, data, reload } = useReadModel<KeyedReportLoad>(
    async () => {
      if (supportLoading) return { client, requestKey, outcome: { kind: "read" as const, value: { kind: "unavailable" as const, reason: "unavailable" as const } } };
      try {
        const value = await fetchReportScanList(client, accountId, {
          siteNames,
          guardName: guardFilter || null,
          routeName: routeName || null,
          checkpointName: checkpointName || null,
          methods,
          presenceAdhocOnly,
          lifecycleStates,
          reviewRequiredOnly,
          dateSelection,
          timeZone,
          now: clock(),
          sort,
        });
        return { client, requestKey, outcome: { kind: "read", value } };
      } catch {
        return {
          client,
          requestKey,
          outcome: { kind: "error", message: "Could not load the scan report." },
        };
      }
    },
    [client, requestKey],
    pollMs,
  );
  const currentLoad = data?.client === client && data.requestKey === requestKey ? data : null;

  useLayoutEffect(() => {
    const bounds = reportPickerBounds(timeZone, clock());
    setSiteNames([]);
    setGuardFilter("");
    setRouteName("");
    setCheckpointName("");
    setMethods([]);
    setPresenceAdhocOnly(false);
    setLifecycleStates([]);
    setReviewRequiredOnly(false);
    setColumns(DEFAULT_REPORT_COLUMNS);
    setSort(DEFAULT_REPORT_SORT);
    setDateSelection(DEFAULT_REPORT_DATE_SELECTION);
    setCustomOpen(false);
    setCustomFromDay(bounds.floorDay);
    setCustomToDay(bounds.todayDay);
    setSiteOptions([]);
    setGuardOptions([]);
    setRouteOptions([]);
    setCheckpointOptions([]);
    setDefaultApplied(false);
    setViewsLoad({ kind: "loading" });
    setStaleView(null);
    validationAttempt.current += 1;
    setPendingValidation(null);
    setSiteCapNotice(null);
    setExportState({ kind: "idle" });
  }, [client, accountId, timeZone, supportScope]);

  // O-3: load default saved view before treating the desk as settled.
  useEffect(() => {
    let cancelled = false;
    if (supportLoading) return;
    if (supportActive) {
      setViewsLoad({ kind: "unavailable" });
      setDefaultApplied(true);
      return;
    }
    setViewsLoad({ kind: "loading" });
    void listMyReportViews(client, accountId).then((result) => {
      if (cancelled) return;
      if (result.status !== "ok") {
        setViewsLoad({ kind: "unavailable" });
        setDefaultApplied(true);
        return;
      }
      setViewsLoad({ kind: "ready", views: result.views });
      // This non-cancelled effect belongs to the current scope. Its closure can
      // still carry the previous scope's settled flag, so never use it to gate settlement.
      const defaultView = result.views.find((view) => view.isDefault);
      if (defaultView !== undefined) {
        applySavedView(defaultView);
      }
      setDefaultApplied(true);
    });
    return () => {
      cancelled = true;
    };
  }, [client, accountId, supportActive, supportLoading, supportScope]);

  useEffect(() => {
    let cancelled = false;
    if (supportLoading) return;
    void fetchReportScanList(client, accountId, {
      dateSelection,
      timeZone,
      now: clock(),
    })
      .then((read) => {
        if (cancelled || read.kind !== "ok") return;
        setSiteOptions((current) => mergeScanLabels(current, uniqueScanLabels(read.rows, "siteName")));
        setGuardOptions((current) =>
          mergeScanLabels(current, uniqueScanLabels(read.rows, "guardName")),
        );
        setRouteOptions((current) =>
          mergeScanLabels(current, uniqueScanLabels(read.rows, "routeName")),
        );
      })
      .catch(() => {
        // The table owns the visible error state; picker options stay at their last safe values.
      });
    return () => {
      cancelled = true;
    };
  }, [client, accountId, dateKey, timeZone, supportScope]);

  useEffect(() => {
    setCheckpointOptions([]);
  }, [routeName]);

  useEffect(() => {
    if (currentLoad?.outcome.kind !== "read" || currentLoad.outcome.value.kind !== "ok") return;
    const rows = currentLoad.outcome.value.rows;
    setSiteOptions((current) => mergeScanLabels(current, uniqueScanLabels(rows, "siteName")));
    setGuardOptions((current) => mergeScanLabels(current, uniqueScanLabels(rows, "guardName")));
    setRouteOptions((current) => mergeScanLabels(current, uniqueScanLabels(rows, "routeName")));
    if (routeName.length > 0) {
      const forRoute = rows
        .filter((row) => row.routeName === routeName)
        .map((row) => row.checkpointName)
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0);
      setCheckpointOptions((current) => mergeScanLabels(current, forRoute));
    } else {
      setCheckpointOptions([]);
    }
  }, [currentLoad, routeName]);

  useEffect(() => {
    setExportState({ kind: "idle" });
  }, [requestKey]);

  const pickerBounds = reportPickerBounds(timeZone, clock());
  const reportRead = currentLoad?.outcome.kind === "read" ? currentLoad.outcome.value : null;
  const rawRows = reportRead?.kind === "ok" ? reportRead.rows : [];
  const rows =
    reportRead?.kind === "ok" ? sortReportScanRows(rawRows, sort) : [];
  const capped = reportRead?.kind === "ok" ? reportRead.capped : false;
  const viewsPending = viewsLoad.kind === "loading" || !defaultApplied;
  const currentPending =
    supportLoading || pendingValidation !== null || viewsPending || status === "loading" || currentLoad === null;
  const currentError = currentLoad?.outcome.kind === "error" ? currentLoad.outcome.message : null;
  const scansLabel = rows.length === 1 ? "1 scan" : `${rows.length} scans`;
  const downloadLabel = capped
    ? `Download CSV (most recent ${REPORT_SCAN_LIMIT})`
    : `Download CSV (${scansLabel})`;
  const siteFilterLabel = reportSitesChipValue(siteNames);
  const windowCopy = reportsResultCopy({
    shownCount: rows.length,
    siteFilter: siteFilterLabel,
    guardFilter,
    dateSelection,
    cappedSorted: capped,
  });
  const filterChips = reportsFilterChips({
    siteNames,
    guardFilter,
    routeName,
    checkpointName,
    methods,
    presenceAdhocOnly,
    lifecycleStates,
    reviewRequiredOnly,
    dateSelection,
  });
  const tableColumns = visibleColumns(columns).filter((column) => {
    if (column === "site" && siteNames.length === 1) return false;
    return true;
  });
  const canExport =
    !supportLoading &&
    !supportActive &&
    !currentPending &&
    currentError === null &&
    reportRead?.kind === "ok" &&
    rows.length > 0 &&
    staleView === null;
  const customSelectable = isReportCustomRangeSelectable(
    customFromDay,
    customToDay,
    pickerBounds,
  );

  function applySavedView(view: ReportSavedView): void {
    setSiteNames(view.filters.siteNames);
    setGuardFilter(view.filters.guardName ?? "");
    setRouteName(view.filters.routeName ?? "");
    setCheckpointName(view.filters.checkpointName ?? "");
    setMethods(view.filters.methods);
    setPresenceAdhocOnly(view.filters.presenceAdhocOnly);
    setLifecycleStates(view.filters.lifecycleStates);
    setReviewRequiredOnly(view.filters.reviewRequiredOnly);
    setDateSelection(view.filters.dateSelection);
    setColumns(view.filters.columns);
    setSort(view.filters.sort);
    setStaleView(null);
    setViewsMenuOpen(false);
    setPendingValidation({
      attempt: ++validationAttempt.current,
      client,
      requestKey: reportRequestKey(accountId, timeZone, supportScope, view.filters),
      view,
    });
  }

  useEffect(() => {
    if (pendingValidation === null) return;
    const pending = pendingValidation;
    if (pending.client !== client || pending.requestKey !== requestKey) {
      validationAttempt.current += 1;
      setPendingValidation(null);
      return;
    }
    let active = true;
    void (async () => {
      let validation: Awaited<ReturnType<typeof validateMyReportViewLabels>>;
      try {
        validation = await validateMyReportViewLabels(client, {
          accountId,
          labels: {
            siteNames: pending.view.filters.siteNames,
            guardName: pending.view.filters.guardName,
            routeName: pending.view.filters.routeName,
            checkpointName: pending.view.filters.checkpointName,
          },
        });
      } catch {
        validation = { status: "unavailable", reason: "unavailable" };
      }
      if (!active || validationAttempt.current !== pending.attempt
        || currentClientRef.current !== pending.client || currentRequestKeyRef.current !== pending.requestKey) return;
      setPendingValidation(null);
      if (validation.status === "unavailable") {
        setStaleView({ kind: "unavailable", message: "Saved-view filters are unavailable right now. Try again." });
      } else if (validation.status === "stale") {
        const unmatched = validation.unmatched;
        const detail = unmatched.map((item) => `${item.family} · ${item.label}`).join("; ");
        setStaleView({
          kind: "stale", unmatched, view: pending.view,
          message: detail.length > 0
            ? `This saved view has labels that no longer match: ${detail}.`
            : "This saved view has labels that no longer match.",
        });
      }
    })();
    return () => { active = false; };
  }, [pendingValidation, client, requestKey]);

  function dropStaleFilters(): void {
    if (staleView === null || staleView.kind !== "stale") return;
    const unmatchedSites = new Set(
      staleView.unmatched
        .filter((item) => item.family === "siteNames")
        .map((item) => item.label),
    );
    if (unmatchedSites.size > 0) {
      setSiteNames((current) => current.filter((name) => !unmatchedSites.has(name)));
    }
    for (const item of staleView.unmatched) {
      if (item.family === "guardName") setGuardFilter("");
      if (item.family === "routeName") {
        setRouteName("");
        setCheckpointName("");
      }
      if (item.family === "checkpointName") setCheckpointName("");
    }
    setStaleView(null);
  }

  function openEditView(): void {
    setColumnsMenuOpen(false);
    setViewsMenuOpen(true);
    queueMicrotask(() => viewsTriggerRef.current?.focus());
  }

  async function retryStaleValidation(): Promise<void> {
    if (staleView === null || staleView.kind !== "unavailable") return;
    const current: ReportSavedView = {
      viewRef: "retry",
      viewId: null,
      name: "Current filters",
      isDefault: false,
      updatedAt: new Date(0).toISOString(),
      filters: {
        siteNames,
        guardName: guardFilter.length > 0 ? guardFilter : null,
        routeName: routeName.length > 0 ? routeName : null,
        checkpointName: checkpointName.length > 0 ? checkpointName : null,
        methods,
        presenceAdhocOnly,
        lifecycleStates,
        reviewRequiredOnly,
        dateSelection,
        columns,
        sort,
      },
    };
    await applySavedView(current);
  }

  function selectPreset(preset: ReportDatePreset): void {
    setDateSelection({ preset });
    setCustomOpen(false);
  }

  function resetDates(): void {
    setDateSelection(DEFAULT_REPORT_DATE_SELECTION);
    setCustomOpen(false);
  }

  function clearAll(): void {
    validationAttempt.current += 1;
    setPendingValidation(null);
    setSiteNames([]);
    setGuardFilter("");
    setRouteName("");
    setCheckpointName("");
    setMethods([]);
    setPresenceAdhocOnly(false);
    setLifecycleStates([]);
    setReviewRequiredOnly(false);
    setColumns(DEFAULT_REPORT_COLUMNS);
    setSort(DEFAULT_REPORT_SORT);
    setStaleView(null);
    setSiteCapNotice(null);
    resetDates();
  }

  function removeFilterChip(label: ConsoleFilterChipLabel): void {
    if (label === "Site" || label === "Sites") {
      setSiteNames([]);
      queueMicrotask(() =>
        (guardFilter.length > 0 ? guardRemoveRef.current : null)?.focus(),
      );
      return;
    }
    if (label === "Guard") {
      setGuardFilter("");
      return;
    }
    if (label === "Route") {
      setRouteName("");
      setCheckpointName("");
      return;
    }
    if (label === "Checkpoint") {
      setCheckpointName("");
      return;
    }
    if (label === "Method") {
      setMethods([]);
      return;
    }
    if (label === "Presence") {
      setPresenceAdhocOnly(false);
      return;
    }
    if (label === "Status") {
      setLifecycleStates([]);
      return;
    }
    if (label === "Review required") {
      setReviewRequiredOnly(false);
    }
  }

  function handleUnavailableAction(action: ConsoleUnavailableAction): void {
    if (action === "back_to_last_90_days") {
      resetDates();
    }
  }

  function applyCustomRange(): void {
    if (!customSelectable) return;
    setDateSelection({ preset: "custom", fromDay: customFromDay, toDay: customToDay });
    closeCustomRange();
  }

  function closeCustomRange(): void {
    setCustomOpen(false);
    customTriggerRef.current?.focus();
  }

  function toggleSite(next: string): void {
    const result = toggleReportSite(siteNames, next);
    if (!result.ok) {
      setSiteCapNotice(REPORT_TOO_MANY_SITES_COPY);
      return;
    }
    setSiteCapNotice(null);
    setSiteNames(result.siteNames);
  }

  function toggleColumn(key: ReportColumnKey): void {
    if (columns.includes(key)) {
      if (columns.length <= 1) return;
      setColumns(columns.filter((column) => column !== key));
      return;
    }
    setColumns([...columns, key]);
  }

  function toggleSort(column: ReportColumnKey): void {
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  }

  async function saveCurrentView(): Promise<void> {
    if (supportActive || viewsLoad.kind !== "ready") return;
    const name = window.prompt("Name this view");
    if (name === null || name.trim().length === 0) return;
    const result = await upsertMyReportView(client, {
      accountId,
      name: name.trim(),
      filters: {
        siteNames,
        guardName: guardFilter || null,
        routeName: routeName || null,
        checkpointName: checkpointName || null,
        methods,
        presenceAdhocOnly,
        lifecycleStates,
        reviewRequiredOnly,
        dateSelection,
        columns,
        sort,
      },
    });
    if (result.status === "ok") {
      setViewsLoad({
        kind: "ready",
        views: [...viewsLoad.views.filter((view) => view.viewRef !== result.view.viewRef), result.view],
      });
    }
  }

  async function handleDownload(): Promise<void> {
    if (exportState.kind === "preparing") return;
    if (supportLoading || supportActive || support.session !== null) {
      setExportState({ kind: "refused", message: REPORT_SUPPORT_CSV_UNAVAILABLE_COPY });
      return;
    }
    if (!canExport || reportRead?.kind !== "ok") return;
    const exportRequestKey = requestKey;
    const exportClient = client;
    const exportedAt = clock();
    const fileName = reportFileName(exportedAt, timeZone);
    setExportState({ kind: "preparing" });
    try {
      if (support.session !== null) {
        setExportState({ kind: "refused", message: REPORT_SUPPORT_CSV_UNAVAILABLE_COPY });
        return;
      }
      const csv = buildScanReportCsv(rows, {
        timeZone,
        columns: tableColumns,
        metadata: {
          site: siteFilterLabel || "All sites",
          sites: siteFilterLabel || "All sites",
          guard: guardFilter || "All guards",
          route: routeName || "All routes",
          checkpoint: checkpointName || "Any on route",
          method: reportMethodsMetadataLabel(methods),
          presence: presenceAdhocOnly ? "Here (no pin)" : "All",
          status: reportStatusesMetadataLabel(lifecycleStates),
          reviewRequired: reviewRequiredOnly ? "Yes" : "No",
          dates: reportDateMetadataLabel(dateSelection, reportRead.range),
          sort: reportSortMetadataLabel(sort),
          columns: reportColumnsMetadataLabel(tableColumns),
          capped,
          exportedAt,
        },
      });
      if (currentClientRef.current !== exportClient || currentRequestKeyRef.current !== exportRequestKey || support.session !== null) {
        setExportState({ kind: "refused", message: REPORT_SUPPORT_CSV_UNAVAILABLE_COPY });
        return;
      }
      await deliverBlobFile({
        blob: createScanReportBlob(csv),
        fileName,
        title: "SecOps scan report",
      });
      if (currentClientRef.current === exportClient && currentRequestKeyRef.current === exportRequestKey) {
        analytics.capture("report_exported", {
          rows: rows.length,
          capped,
          site_filtered: siteNames.length > 0,
          guard_filtered: guardFilter.length > 0,
          dates: dateSelection.preset,
        });
        setExportState({ kind: "success", fileName });
      }
    } catch {
      if (currentClientRef.current === exportClient && currentRequestKeyRef.current === exportRequestKey) {
        setExportState({ kind: "error" });
      }
    }
  }

  return (
    <div className="report">
      <div className="report__toolbar">
        <label className="report__filter">
          <span className="report__filter-label">Sites</span>
          <select
            aria-label="Filter by sites"
            value=""
            onChange={(event) => {
              if (event.target.value.length === 0) return;
              toggleSite(event.target.value);
              event.target.value = "";
            }}
          >
            <option value="">
              {siteNames.length === 0
                ? "All sites"
                : `${siteNames.length} selected (max ${REPORT_SITE_SELECTION_MAX}) · pick again to remove`}
            </option>
            {siteOptions.map((site) => (
              <option key={site} value={site}>
                {siteNames.includes(site) ? `✓ ${site}` : site}
              </option>
            ))}
          </select>
        </label>
        <label className="report__filter">
          <span className="report__filter-label">Guard</span>
          <select
            aria-label="Filter by guard"
            value={guardFilter}
            onChange={(event) => setGuardFilter(event.target.value)}
          >
            <option value="">All guards</option>
            {guardOptions.map((guard) => (
              <option key={guard} value={guard}>
                {guard}
              </option>
            ))}
          </select>
        </label>
        <label className="report__filter">
          <span className="report__filter-label">Route</span>
          <select
            aria-label="Filter by route"
            value={routeName}
            onChange={(event) => {
              setRouteName(event.target.value);
              setCheckpointName("");
            }}
          >
            <option value="">All routes</option>
            {routeOptions.map((route) => (
              <option key={route} value={route}>
                {route}
              </option>
            ))}
          </select>
        </label>
        <label className="report__filter">
          <span className="report__filter-label">Checkpoint</span>
          <select
            aria-label="Filter by checkpoint"
            value={checkpointName}
            disabled={routeName.length === 0}
            onChange={(event) => setCheckpointName(event.target.value)}
          >
            <option value="">{routeName.length === 0 ? "Any on route" : "Any on route"}</option>
            {checkpointOptions.map((checkpoint) => (
              <option key={checkpoint} value={checkpoint}>
                {checkpoint}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="report__dates">
          <legend className="report__filter-label">Dates</legend>
          <div className="report__date-presets">
            {REPORT_DATE_PRESETS.map((preset) => {
              const selected = dateSelection.preset === preset.value;
              return (
                <button
                  className={capped && !selected ? "report__date-preset--suggested" : undefined}
                  key={preset.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => selectPreset(preset.value)}
                >
                  {preset.label}
                </button>
              );
            })}
            <button
              className={capped && dateSelection.preset !== "custom" ? "report__date-preset--suggested" : undefined}
              ref={customTriggerRef}
              type="button"
              aria-pressed={dateSelection.preset === "custom"}
              aria-expanded={customOpen}
              aria-controls={customPanelId}
              onClick={() => setCustomOpen((open) => !open)}
            >
              Custom
            </button>
          </div>
          {customOpen ? (
            <div
              className="report__custom"
              id={customPanelId}
              role="group"
              aria-label="Custom date range"
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                event.preventDefault();
                closeCustomRange();
              }}
            >
              <p className="report__custom-title">
                Custom range <span>{timeZone} · workspace display zone</span>
              </p>
              <div className="report__custom-fields">
                <label>
                  <span>From</span>
                  <input
                    aria-label="Custom date from"
                    type="date"
                    min={pickerBounds.floorDay}
                    max={pickerBounds.todayDay}
                    value={customFromDay}
                    onChange={(event) => setCustomFromDay(event.target.value)}
                  />
                </label>
                <label>
                  <span>To</span>
                  <input
                    aria-label="Custom date to"
                    type="date"
                    min={pickerBounds.floorDay}
                    max={pickerBounds.todayDay}
                    value={customToDay}
                    onChange={(event) => setCustomToDay(event.target.value)}
                  />
                </label>
              </div>
              <p className="report__custom-floor">{REPORT_PICKER_FLOOR_COPY}</p>
              <div className="report__custom-actions">
                <button type="button" onClick={closeCustomRange}>Cancel</button>
                <button type="button" disabled={!customSelectable} onClick={applyCustomRange}>Apply</button>
              </div>
            </div>
          ) : null}
        </fieldset>
        <div className="report__attrs" role="group" aria-label="Attribute filters">
          {REPORT_SCAN_METHODS.map((method) => (
            <button
              key={method}
              type="button"
              aria-pressed={methods.includes(method)}
              onClick={() => setMethods(toggleEnumValue(methods, method))}
            >
              {REPORT_METHOD_LABELS[method]}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={presenceAdhocOnly}
            onClick={() => setPresenceAdhocOnly((value) => !value)}
          >
            Here (no pin)
          </button>
          {REPORT_LIFECYCLE_STATES.map((state) => (
            <button
              key={state}
              type="button"
              aria-pressed={lifecycleStates.includes(state)}
              onClick={() => setLifecycleStates(toggleEnumValue(lifecycleStates, state))}
            >
              {REPORT_LIFECYCLE_LABELS[state]}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={reviewRequiredOnly}
            onClick={() => setReviewRequiredOnly((value) => !value)}
          >
            Review required
          </button>
        </div>
        <div className="report__secondary">
          <div className="report__menu">
            <button
              ref={viewsTriggerRef}
              type="button"
              aria-expanded={viewsMenuOpen}
              aria-haspopup="menu"
              onClick={() => {
                setColumnsMenuOpen(false);
                setViewsMenuOpen((open) => !open);
              }}
            >
              Views
            </button>
            {viewsMenuOpen ? (
              <div className="report__menu-panel" role="menu">
                {viewsLoad.kind === "ready" ? (
                  viewsLoad.views.length === 0 ? (
                    <p>No saved views yet.</p>
                  ) : (
                    viewsLoad.views.map((view) => (
                      <button
                        key={view.viewRef}
                        type="button"
                        role="menuitem"
                        onClick={() => void applySavedView(view)}
                      >
                        {view.name}
                        {view.isDefault ? " (default)" : ""}
                      </button>
                    ))
                  )
                ) : (
                  <p>Views unavailable.</p>
                )}
                <button type="button" role="menuitem" onClick={() => void saveCurrentView()}>
                  Save current
                </button>
              </div>
            ) : null}
          </div>
          <div className="report__menu">
            <button
              type="button"
              aria-expanded={columnsMenuOpen}
              aria-haspopup="menu"
              onClick={() => {
                setViewsMenuOpen(false);
                setColumnsMenuOpen((open) => !open);
              }}
            >
              Columns
            </button>
            {columnsMenuOpen ? (
              <div className="report__menu-panel" role="menu">
                {REPORT_COLUMN_KEYS.map((key) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={columns.includes(key)}
                      disabled={columns.includes(key) && columns.length === 1}
                      onChange={() => toggleColumn(key)}
                    />
                    {REPORT_COLUMN_LABELS[key]}
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <button
          className="report__download"
          type="button"
          disabled={(!canExport && !supportActive) || exportState.kind === "preparing"}
          title={supportActive ? REPORT_SUPPORT_CSV_UNAVAILABLE_COPY : undefined}
          onClick={() => void handleDownload()}
        >
          {exportState.kind === "preparing" ? "Preparing CSV…" : downloadLabel}
        </button>
      </div>

      {siteCapNotice !== null ? (
        <p className="report__note" role="status">{siteCapNotice}</p>
      ) : null}
      {routeName.length > 0 ? (
        <p className="report__note">{REPORT_CHECKPOINT_WINDOW_HINT}</p>
      ) : null}

      <FilterStrip
        chips={filterChips}
        onClearAll={clearAll}
        onRemoveChip={removeFilterChip}
        note="reports_print_hint"
        chipRemoveRefs={{
          Site: sitesRemoveRef,
          Sites: sitesRemoveRef,
          Guard: guardRemoveRef,
        }}
      />

      <WindowLine variant={CONSOLE_WINDOW_SCANS} about="reports_scans" />

      {exportState.kind === "error" ? (
        <p className="surface__state surface__state--error" role="alert">
          The CSV could not be shared or downloaded. Try again.
        </p>
      ) : null}
      {exportState.kind === "refused" ? (
        <p className="surface__state surface__state--error" role="alert">
          {exportState.message}
        </p>
      ) : null}
      {exportState.kind === "success" ? (
        <p className="report__exported" role="status">Exported · {exportState.fileName}</p>
      ) : null}
      {staleView !== null ? (
        <div className="report__state-pane report__state-pane--stale" role="status">
          <div>
            <p className="report__state-kicker">Saved view needs attention</p>
            <p className="surface__state">{staleView.message}</p>
            <div className="report__state-actions">
              {staleView.kind === "stale" ? (
                <button type="button" onClick={dropStaleFilters}>
                  Drop that filter
                </button>
              ) : (
                <button type="button" onClick={() => void retryStaleValidation()}>
                  Try again
                </button>
              )}
              <button type="button" onClick={openEditView}>
                Edit view
              </button>
            </div>
          </div>
        </div>
      ) : currentPending ? (
        <div className="report__state-pane"><p className="surface__state" role="status">Loading scan report…</p></div>
      ) : currentError !== null ? (
        <div className="report__state-pane">
          <div>
            <p className="surface__state surface__state--error" role="alert">{currentError}</p>
            <button className="report__retry" type="button" onClick={reload}>Retry</button>
          </div>
        </div>
      ) : reportRead?.kind === "unavailable" && reportRead.reason === "too_many_sites" ? (
        <UnavailableNotice surface="reports" reason="too_many_sites" kicker="unavailable_not_empty" />
      ) : reportRead?.kind === "unavailable" ? (
        <UnavailableNotice
          surface="reports"
          reason={reportRead.reason === "self_serve_floor" ? "self_serve_floor" : "unavailable"}
          kicker="unavailable_not_empty"
          {...(reportRead.reason === "self_serve_floor"
            ? {
                actions: REPORTS_SELF_SERVE_UNAVAILABLE_ACTIONS,
                onAction: handleUnavailableAction,
              }
            : {})}
        />
      ) : reportRead?.kind === "ok" ? (
        <div className="report__table-pane">
          {rows.length === 0 ? (
            <div className="report__empty" role="status">
              <div>
                <p className="report__state-kicker">Empty</p>
                <p>{windowCopy.foot}</p>
                <button type="button" onClick={clearAll}>Clear all</button>
              </div>
            </div>
          ) : (
            <div className="report__scroll">
              <table className="report__table">
                <thead>
                  <tr>
                    {tableColumns.map((column) => (
                      <th scope="col" key={column}>
                        <button type="button" onClick={() => toggleSort(column)}>
                          {column === "captured"
                            ? "Captured"
                            : column === "gps"
                              ? "GPS"
                              : REPORT_COLUMN_LABELS[column]}
                          {sort.column === column ? (sort.direction === "asc" ? " ↑" : " ↓") : ""}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.clientEventId || `${row.serverReceivedAt}-${index}`}>
                      {tableColumns.map((column) => (
                        <td
                          key={column}
                          className={
                            column === "captured"
                              ? "report__time"
                              : column === "checkpoint"
                                ? "report__cp"
                                : undefined
                          }
                        >
                          {cellText(row, column, timeZone)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.length > 0 ? <ResultFooter state={windowCopy.state} foot={windowCopy.foot} /> : null}
        </div>
      ) : null}
    </div>
  );
}
